/** Wire protocol shared (in shape) with `server/index.mjs`. */
import type { VariantId, BallId, ThemeId, PhysicsConstants } from '../config';
import type { EnvId } from '../render/Environments';
import type { BallSnapshot, CueParams } from '../types';
import type { MatchState, ShotResult } from '../rules';

export type Seat = 0 | 1;

/** Room match format: how many teams and how many players per team. */
export type GameFormat = '1v1' | '2v2' | '1v1v1' | '1v1v1v1';

/** How the opening is set up: a fixed preset layout, or the interactive cue swing. */
export type BreakStyle = 'preset' | 'swing';

/** Teams / per-team / total-player counts for each format. Shared by client + server. */
export const FORMAT_INFO: Record<GameFormat, { teams: number; perTeam: number; players: number }> = {
  '1v1': { teams: 2, perTeam: 1, players: 2 },
  '2v2': { teams: 2, perTeam: 2, players: 4 },
  '1v1v1': { teams: 3, perTeam: 1, players: 3 },
  '1v1v1v1': { teams: 4, perTeam: 1, players: 4 },
};

/**
 * House "마지막 3쿠션" finish rule for 4-ball: after clearing the handicap, how many
 * qualifying 3-cushion caroms win the game, and how many of those must be bank
 * (빈쿠션 — cue contacts a cushion before the first object ball) shots.
 */
export interface FinishRule {
  /** Qualifying final 3-cushion caroms needed to win (1 or 2). */
  caroms: number;
  /** Of those, how many must be bank shots (0 or 1). */
  banks: number;
}

/** Default finish rule by format: 2v2 demands two caroms incl. one bank; else one. */
export function defaultFinish(format: GameFormat): FinishRule {
  return format === '2v2' ? { caroms: 2, banks: 1 } : { caroms: 1, banks: 0 };
}

/** Who you are in the room. `name` is your nickname (guest or registered). */
export interface Identity {
  name: string;
  /** Stable account id (guest or registered) — the ranking key. */
  id?: string;
  /** Korean handicap (points to clear) the player brings into the room. */
  level?: number;
  /** Serialized profile avatar (see avatar/index.ts), shown on the seat. */
  avatar?: string;
}

/** A room as it appears in the browser list (one line per open room). */
export interface RoomInfo {
  code: string;
  name: string;
  format: GameFormat;
  variant: VariantId;
  players: number; // seated players currently present
  capacity: number; // FORMAT_INFO[format].players
  status: 'waiting' | 'playing';
  /** Names of the seated players (for the room-list display). */
  playerNames: string[];
  /** Set on fakeActiveRooms padding rows — not a real, joinable session. */
  stub?: boolean;
  /** The room's 코인 stake; 0 = no bet. You can't take a seat you can't cover. */
  stake: number;
  /** Host's ladder tier, set only when `stake` > 0 — joining a seat then
   *  requires being within the server's rank-gap limit of this tier. */
  hostTier: number | null;
}

/** One participant inside a waiting room / game (player or spectator). */
export interface LobbyMember {
  id: string; // stable per-connection id assigned by the server
  name: string;
  team: number; // 0-based team index; -1 for spectators
  role: 'player' | 'spectator';
  ready: boolean;
  host: boolean;
  level: number; // Korean handicap (points to clear), editable while waiting
  avatar?: string; // serialized profile avatar (empty for spectators)
  /** Seat index (room.seats position) for players; -1 for spectators. */
  seat: number;
  /** False when this seat's 코인 balance can't cover the room's stake (players only). */
  staked?: boolean;
}

/** Full waiting-room state, pushed to everyone in the room on any change. */
export interface LobbyState {
  code: string;
  name: string;
  format: GameFormat;
  variant: VariantId;
  status: 'waiting' | 'playing';
  hostId: string;
  youId: string;
  members: LobbyMember[];
  config: RoomConfig | null;
  breakStyle: BreakStyle;
  /** Would this start as a ranked game? (everyone seated is at their own tier —
   *  normally always true; only false if a tier changed mid-wait) */
  ranked?: boolean;
  /** Host-chosen 코인 stake; 0 = no bet. The loser pays this into a pot the
   *  winner(s) split when the game ends. */
  stake: number;
}

/**
 * Global presence: what a connected player is currently doing. Reported by each
 * client over a dedicated always-on socket and rebroadcast to everyone, so the
 * online-players panel can list who's around and their status.
 */
export type PresenceStatus =
  | 'standby' // in the menus / room browser, not playing
  | 'solo' // single-play practice
  | 'ai' // single-play vs the computer
  | 'tutorial' // working through a 당구 교실 lesson
  | 'waiting' // sitting in an online waiting room
  | 'spectating' // watching an online match
  | 'playing-1v1'
  | 'playing-2v2'
  | 'playing-1v1v1'
  | 'playing-1v1v1v1';

/** One entry in the online-players list. */
export interface PresenceUser {
  name: string;
  status: PresenceStatus;
  /** Room code of the match this player is in, when it's watchable (playing). */
  room?: string;
}

/**
 * Where a player stands on the 점 ladder RIGHT NOW — the numbers behind a
 * promotion, surfaced so the ladder is visible instead of teleporting you to the
 * next tier one day unannounced. All of it is measured, none of it is Elo.
 */
export interface TierProgress {
  /** Current handicap tier (점). */
  tier: number;
  /** The next / previous rung, or null at the ends of the ladder. */
  next: number | null;
  prev: number | null;
  /** Rolling 애버리지 (점 per inning) over the recent games at this tier. */
  avg: number;
  /** Innings and games behind that average. */
  innings: number;
  games: number;
  /** False until `trustInnings` innings have accumulated — the average is provisional. */
  trusted: boolean;
  trustInnings: number;
  /** The continuous 점 the measured average currently earns. */
  level: number;
  /**
   * Target averages that bracket you: relegation fires at `bandPrev`, promotion
   * at `bandNext`, and `bandNow` is your own band's mark in between.
   */
  bandPrev: number;
  bandNow: number;
  bandNext: number | null;
  /** Consecutive qualifying games banked toward promotion, and how many are needed. */
  promo: number;
  promoNeed: number;
  /** The same counters for relegation (slow by design). */
  demo: number;
  demoNeed: number;
}

/** Win/loss record against one specific opponent, from my point of view. */
export interface HeadToHead {
  wins: number;
  losses: number;
  /** When we last played (0 = never). */
  lastTs: number;
  /** Did I win that last one? */
  lastWon: boolean;
}

/** One row of the 이번주의 미션 board. `strokes` null = attempted but never cleared. */
export interface DailyEntry {
  rank: number;
  id: string;
  name: string;
  strokes: number | null;
  caroms: number;
  /** 'timedSpeed' only: best clear time, ms. Null = never cleared. */
  elapsedMs: number | null;
  /** Coin award this rank earns at Monday rollover (0 outside the top 5). */
  coins: number;
}

/** My own runs at today's challenge. */
export interface DailyRun {
  /** Best (fewest) strokes to clear; null if never cleared. */
  strokes: number | null;
  /** Best caroms made in a single attempt. */
  caroms: number;
  /** 'timedSpeed' only: best clear time, ms. Null = never cleared. */
  elapsedMs: number | null;
  attempts: number;
  /** Position on today's board (0 = unranked). */
  rank: number;
}

/** A CheckTemplateId from src/tutorial/checkTemplates.ts, kept as a bare string on the wire. */
export type TutorialCheckType = 'always' | 'hitAny' | 'softHit' | 'hardHit' | 'scored' | 'bank' | 'tipThreshold' | 'masse';

/** A brand-new, admin-authored tutorial lesson (checked via a template, not code). */
export interface TutorialLessonWire {
  id: string;
  variant: VariantId;
  chapter: string;
  orderIndex: number;
  layout: Record<string, { x: number; y: number }>;
  /** Starting cue-ball aim angle override, radians. Null = auto-face the nearest object ball. */
  cueAngle: number | null;
  checkType: TutorialCheckType;
  checkParams: Record<string, number>;
  hint: { x: number; y: number }[] | null;
  desktopOnly: boolean;
  retryLayout: boolean;
  titleKo: string; titleEn: string;
  goalKo: string; goalEn: string;
  keysKo: string; keysEn: string;
  tipKo: string; tipEn: string;
  retryKo: string; retryEn: string;
  updatedAt: number;
}

/** A layout/order/copy patch onto one BUILT-IN lesson id — nulls keep the default. */
export interface TutorialOverrideWire {
  id: string;
  orderIndex: number | null;
  layout: Record<string, { x: number; y: number }> | null;
  /** Starting cue-ball aim angle override, radians. Null = keep the built-in default. */
  cueAngle: number | null;
  titleKo: string; titleEn: string;
  goalKo: string; goalEn: string;
  tipKo: string; tipEn: string;
  updatedAt: number;
}

/** An admin-scheduled override of one week's 이번주의 미션. */
export interface WeeklyMissionWire {
  weekKey: string;
  variant: VariantId;
  /** 'timedScore' = Time Attack (most caroms in a time limit); 'timedSpeed' = Time Attack (fastest to clear a target). */
  ruleType: 'caroms' | 'banks' | 'timedScore' | 'timedSpeed';
  target: number;
  minBanks: number;
  maxStrokes: number;
  attempts: number;
  /** Normalized (-1..1) layout, or null to keep that week's procedural one. */
  positions: Record<string, { x: number; y: number }> | null;
  /** Starting cue-ball aim angle override, radians. Null = auto-face the nearest object ball. */
  cueAngle: number | null;
  title: string;
  /** 'timedScore' only: the countdown length, seconds. Null for every other rule. */
  timeLimitSec: number | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** One itemized 코인 award (the reward toast lists these). */
export interface CoinEvent {
  kind: 'innings' | 'dailyWin' | 'dailyClear' | 'dailyBest';
  coins: number;
  /** Innings played, for the kinds that count them. */
  n?: number;
}

/**
 * A shot shared into room chat. The engine is deterministic, so the PRE-shot
 * layout plus the cue that was struck is enough for every recipient to replay it
 * exactly — no video, just numbers.
 */
export interface SharedShot {
  variant: VariantId;
  positions: Record<string, { x: number; y: number }>;
  cue: CueParams;
  /** Short caption, e.g. "3쿠션 · 5점째". */
  label: string;
}

/** The local player's own ranking card (shown by the scoreboard). */
export interface MyProfile {
  /** Leaderboard position (1 = top), or 0 if unranked. */
  rank: number;
  /** Handicap tier (점); measured from your 애버리지, starts at 30. */
  tier: number;
  /** Hidden Elo rating. */
  rating: number;
  wins: number;
  losses: number;
  /** Current 코인 balance. */
  coins: number;
  /** Live ladder progress (null if the player has no ladder row yet). */
  progress: TierProgress | null;
}

/** Public all-time stats for a player in a game (shown under their score card). */
export interface PlayerStats {
  id: string;
  rank: number;
  tier: number;
  rating: number;
  wins: number;
  losses: number;
  /** Current 코인 balance. */
  coins: number;
}

/** One row of the top-players leaderboard (Elo rating + record). */
export interface LeaderboardEntry {
  rank: number;
  /** Stable player id (for 'me' matching). */
  id: string;
  name: string;
  /** Hidden skill rating (Elo); ordering is by this. */
  rating: number;
  /** The player's self-declared Korean handicap tier (점). */
  tier: number;
  /** Tier the Elo suggests (a hint; the player isn't forced to it). */
  recommend: number;
  wins: number;
  losses: number;
  /** True for the autonomous AI-run players (shown with a marker). */
  bot: boolean;
}

/**
 * A social sign-in provider the server has credentials for. Sent on `authed`;
 * an empty list means the server can't complete any social login, so the UI
 * shows no buttons rather than ones that dead-end.
 */
export interface AuthProvider {
  id: 'google' | 'kakao';
  label: string;
}

/** Result posted back from the OIDC popup (see server/authroutes.mjs). */
export interface OidcResult {
  token?: string;
  id?: string;
  name?: string;
  provider?: string;
  /** existing = returning user · merged = linked by verified email ·
   *  claimed = upgraded the guest in place · created = brand-new account */
  linked?: 'existing' | 'merged' | 'claimed' | 'created';
  error?: string;
}

// ---- admin oversight (gated to admin accounts) ----
export interface AdminMember {
  /** Stable account/bot id (for the history drill-down). */
  id?: string;
  name: string;
  status: PresenceStatus;
  room: string | null;
  registered?: boolean;
  bot?: boolean;
  /** Ranking fields when the player is in the ladder (humans + bots). */
  rating?: number;
  tier?: number;
  wins?: number;
  losses?: number;
}

/** One row of the global match feed (real played/reported matches). */
export interface AdminMatch {
  ts: number;
  winner: string;
  loser: string;
  winnerDelta: number;
  loserDelta: number;
  botMatch: boolean;
}

/** One player's match from their own POV (for the history drill-down). */
export interface PlayerMatch {
  ts: number;
  opponent: string;
  won: boolean;
  elo: number;
  delta: number;
}

/** Full per-player profile: rank metrics + recent personal match history. */
export interface PlayerProfile {
  /** Stable account/bot id (needed for the admin tier control). */
  id: string;
  name: string;
  rating: number;
  tier: number;
  recommend: number;
  wins: number;
  losses: number;
  bot: boolean;
  lastPlayed: number;
  matches: PlayerMatch[];
}
export interface AdminSeat {
  id: string;
  name: string;
  level: number;
  ready: boolean;
  team: number;
  present: boolean;
}
export interface AdminChatLine {
  name: string;
  text: string;
  ts: number;
  system?: boolean;
}
export interface AdminRoom {
  code: string;
  name: string;
  format: GameFormat;
  variant: VariantId;
  status: 'waiting' | 'playing';
  hostId: string;
  breakStyle: BreakStyle;
  seats: (AdminSeat | null)[];
  spectators: { id: string; name: string }[];
  chat: AdminChatLine[];
}
export interface AdminSnapshot {
  now: number;
  stats: { connections: number; rooms: number; browsers: number };
  members: AdminMember[];
  rooms: AdminRoom[];
  /** Recent global match feed (newest first). */
  matches: AdminMatch[];
}

// ---- admin console (the separate /admin page) ----
// Reads go through `adminQuery`, mutations through `adminAction`, so the server
// has exactly one place to audit. Query names are a union rather than a free
// string so a typo is a compile error, not an empty panel.

export type AdminQueryName =
  | 'overview' | 'stats' | 'games' | 'game'
  | 'players' | 'player' | 'chat' | 'audit' | 'events' | 'clientPerf'
  | 'missions' | 'tutorials' | 'flags' | 'deploy'
  | 'feedback';

/** One feature gate, as listed/toggled from the admin console. */
export interface FeatureFlag {
  name: string;
  value: boolean;
}

/**
 * One window of client-side render telemetry, aggregated in the browser and
 * flushed periodically — never a per-frame stream.
 *
 * Deliberately low-entropy: the GPU is reduced to a vendor bucket and the device
 * to desktop/touch. The raw `WEBGL_debug_renderer_info` string ("ANGLE (NVIDIA
 * GeForce RTX 3060 ...)") is a strong fingerprint next to a real account, so it
 * is bucketed in the browser and the full string never leaves it.
 *
 * Frame times are milliseconds. p95 matters more than the median here: a 60fps
 * median with a 90ms p95 is the stutter players actually complain about.
 */
export interface ClientPerfSample {
  /** Wall-clock length of the sampled window (ms). */
  windowMs: number;
  /** Frames the loop ran (including ones the on-demand renderer skipped). */
  frames: number;
  /** Fraction of those frames that issued a draw (0..1) — on-demand efficiency. */
  drawn: number;
  /** Median frame interval (ms). */
  medianMs: number;
  /** 95th-percentile frame interval (ms) — the jank signal. */
  p95Ms: number;
  /** Mean draw calls per drawn frame (main + shadow pass). */
  calls: number;
  /** Mean triangles per drawn frame. */
  triangles: number;
  /** Compiled shader programs at flush time. */
  programs: number;
  /** Real (per-pixel) lights in the scene at flush time. */
  lights: number;
  /** Renderer pixel ratio in effect. */
  pixelRatio: number;
  /** Canvas size at flush time, in device pixels — the true fill-rate load. */
  pixels: number;
  /** Which environment was active ('billiardhall' | 'hall' | 'beach' | ...). */
  env: string;
  /** Coarse GPU vendor bucket. */
  gpu: 'nvidia' | 'amd' | 'intel' | 'apple' | 'adreno' | 'mali' | 'other';
  /** Primary pointer class, matching applyEnvClass(). */
  device: 'desktop' | 'touch';
}

/** Admin rollup of client render telemetry, grouped by GPU vendor + device. */
export interface ClientPerfRollup {
  buckets: {
    gpu: string;
    device: string;
    env: string;
    samples: number;
    medianMs: number;
    p95Ms: number;
    calls: number;
    drawn: number;
  }[];
  since: number;
}

export type AdminActionName =
  | 'ban' | 'unban' | 'mute' | 'unmute' | 'setTier' | 'setAdmin' | 'kick'
  | 'saveMission' | 'deleteMission'
  | 'saveTutorialLesson' | 'deleteTutorialLesson' | 'saveTutorialOverride' | 'deleteTutorialOverride'
  | 'setFlag' | 'promote'
  | 'feedbackSetStatus';

export type FeedbackStatus = 'open' | 'closed';

/** One 건의하기 report: a one-way message a user sent to admins. No reply, no thread. */
export interface FeedbackReportWire {
  id: string;
  accountId: string;
  name: string;
  text: string;
  status: FeedbackStatus;
  createdAt: number;
}

/** A day-bucketed count, the shape every time series in the console uses. */
export interface DayCount {
  day: string;
  n: number;
}

export interface AdminOverview {
  now: number;
  live: { connections: number; browsers: number; rooms: number; playing: number };
  dau: number;
  wau: number;
  mau: number;
  accounts: number;
  guests: number;
  admins: number;
  games24h: { games: number; finished: number; avg_innings: number | null; avg_duration: number | null };
}

export interface AdminStats {
  days: number;
  activeByDay: DayCount[];
  signupsByDay: DayCount[];
  guestsByDay: DayCount[];
  gamesByDay: { day: string; started: number; finished: number }[];
  byMode: { mode: string; n: number }[];
  byVariant: { variant: string; n: number }[];
  totals: { games: number; finished: number; avg_innings: number | null; avg_duration: number | null };
  retention: { day: string; n: number; d1: number; d7: number }[];
  purchases: { item_id: string; n: number }[];
}

/** One row of the server-wide game index. */
export interface AdminGame {
  id: string;
  ts: number;
  ended: number | null;
  day: string;
  variant: string;
  mode: string;
  format: string;
  room: string | null;
  ranked: number;
  bot_game: number;
  players: { id: string | null; name: string; tier: number | null; bot?: boolean; index?: number }[];
  winner_id: string | null;
  winner: string | null;
  innings: number | null;
  duration_ms: number | null;
  shot_count: number | null;
  finished: number;
  log_account: string | null;
  log_id: string | null;
}

export interface AdminAccountRow {
  id: string;
  name: string;
  email: string | null;
  guest: number;
  admin: number;
  created: number;
  coins: number;
  banned_until: number;
  muted_until: number;
  ladder?: { rating: number; tier: number; wins: number; losses: number } | null;
}

export interface AdminChatLog {
  id: number;
  ts: number;
  room: string;
  account_id: string | null;
  name: string;
  text: string;
  system: number;
}

export interface AdminAuditRow {
  id: number;
  ts: number;
  actor_id: string;
  actor_name: string;
  action: string;
  target_id: string | null;
  target_name: string | null;
  data: Record<string, unknown> | null;
}

export interface AdminEventRow {
  id: number;
  ts: number;
  day: string;
  kind: string;
  account_id: string | null;
  room: string | null;
  data: Record<string, unknown> | null;
}

/** Result payload for each query name, so callers get a typed `data`. */
export interface AdminQueryResults {
  overview: AdminOverview;
  stats: AdminStats;
  games: { total: number; games: AdminGame[] };
  game: (AdminGame & { log: GameRecord | null }) | null;
  players: { total: number; players: AdminAccountRow[] };
  player: {
    account: AdminAccountRow & { banned: boolean; muted: boolean; banReason: string | null };
    ladder: { rating: number; tier: number; wins: number; losses: number } | null;
    progress: unknown;
    history: PlayerProfile | null;
    games: AdminGame[];
    events: AdminEventRow[];
    chat: AdminChatLog[];
    purchases: { item_id: string; ts: number }[];
    sessions: number;
  } | null;
  chat: { lines: AdminChatLog[] };
  audit: { rows: AdminAuditRow[] };
  events: { kinds: string[]; rows: AdminEventRow[] };
  clientPerf: ClientPerfRollup;
  missions: { missions: WeeklyMissionWire[] };
  tutorials: { lessons: TutorialLessonWire[]; overrides: TutorialOverrideWire[]; builtinIds: string[] };
  flags: { flags: FeatureFlag[] };
  deploy: DeployStatus;
  feedback: { total: number; reports: FeedbackReportWire[] };
}

/** One commit, as carried in a slot's deploy manifest. */
export interface DeployCommit {
  sha: string;
  author: string;
  date: string;
  subject: string;
}

/** The build one environment is currently serving. */
export interface DeployEnvironment {
  env: string;
  /** Blue-green slot letter currently live ('a' | 'b'). */
  slot: string;
  urls: string[];
  sha: string | null;
  branch: string | null;
  /** The working tree had uncommitted changes when this build was shipped. */
  dirty: boolean;
  deployedAt: string | null;
  deployedBy: string | null;
  promotedFrom: string | null;
}

/** Progress of an in-flight (or last finished) promotion. */
export interface DeployPromoteStatus {
  state: 'running' | 'done' | 'error';
  step: string;
  sha: string | null;
  updatedAt: string;
  startedAt?: string;
  finishedAt?: string | null;
}

export interface DeployStatus {
  /** Which environment the console is talking to ('prod' | 'staging' | 'dev'). */
  env: string;
  prod: DeployEnvironment | null;
  staging: DeployEnvironment | null;
  /**
   * Commits staging has that prod doesn't — what Promote would ship. `null`
   * means the two have diverged (or prod is ahead), which is not the same as
   * "nothing to ship" and must not be rendered as an empty list.
   */
  pending: DeployCommit[] | null;
  upToDate: boolean;
  promote: DeployPromoteStatus | null;
  canPromote: boolean;
}

/** One chat line (player text or a system notice), relayed to the whole room. */
export interface ChatLine {
  name: string;
  text: string;
  ts: number;
  system?: boolean;
  /** An attached shot the room can replay (see SharedShot). */
  shot?: SharedShot;
}

/**
 * One private message (귓속말), addressed by nickname and routed over the
 * always-on presence socket — so a DM reaches a player wherever they are: in the
 * menus, browsing rooms, or mid-game. `echo` marks the copy the sender gets back,
 * which is what confirms the message actually went out (the client renders its
 * own message only on that echo).
 */
export interface DmLine {
  from: string;
  to: string;
  text: string;
  ts: number;
  echo?: boolean;
}

/** Everything needed to reproduce a shot on the opponent's client. */
export interface ShotPayload {
  cue: CueParams;
  finalState: Record<BallId, BallSnapshot>;
  result: ShotResult;
  /**
   * Room seat that sent this, stamped by the server (never by the client, so it
   * can't be spoofed). Absent on server-authored shots — the bot exhibition and
   * managed vs-bot games play their turns back through this same message. A
   * receiving client checks it against whose turn it actually is.
   */
  from?: number;
  // Server-authoritative post-shot match state (bot exhibition / managed games only —
  // pure peer-relayed PvP has no server-side match to send). When present, the
  // receiving client adopts it directly instead of re-deriving via applyShotResult,
  // so a spectator can never silently drift from the server's truth shot-by-shot.
  match?: MatchState;
  /**
   * Monotonic per-room counter, stamped by the server on every ball/match-state
   * broadcast (shot/practice/sync/newgame). Lets a receiver notice a broadcast
   * that never arrived (e.g. its socket was mid-reconnect) and ask for a fresh
   * snapshot instead of silently sitting on stale ball positions — see 'resync'.
   */
  seq?: number;
}

/**
 * One recorded shot, kept so it can be reviewed and replayed. Everything here
 * plus the game's stored physics is enough to re-`simulate` the shot exactly:
 * the PRE-shot ball layout + the committed cue. `result` is a small summary for
 * the review list (full scoring is re-derivable but not needed there).
 */
export interface ShotRecord {
  /** 0-based order within the game. */
  seq: number;
  /** Match player/team index that shot. */
  shooterIndex: number;
  shooterName: string;
  inning: number;
  cue: CueParams;
  /** Ball table-plane positions just before the shot (same shape as SerializedGame). */
  positions: Record<BallId, { x: number; y: number }>;
  result: { scored: boolean; points: number; foul: boolean };
}

/** A full recorded game: header + ordered shot log. Stored per account. */
export interface GameRecord {
  /** Client-minted uuid (unique within an account's history). */
  id: string;
  ts: number;
  variant: VariantId;
  mode: 'online' | 'ai' | 'solo';
  format: GameFormat | 'solo';
  /** The exact physics constants the game was played under (for faithful replay). */
  physics: PhysicsConstants;
  players: { name: string; index: number }[];
  winner: string | null;
  shots: ShotRecord[];
}

/** A lightweight game-history row (no shot blobs); expanded via `gameDetail`. */
export interface GameSummary {
  id: string;
  ts: number;
  variant: VariantId;
  mode: GameRecord['mode'];
  format: GameRecord['format'];
  winner: string | null;
  shotCount: number;
  opponents: string[];
}

/** A full game snapshot used to resync a rejoining player or a new spectator. */
export interface SerializedGame {
  variant: VariantId;
  match: MatchState;
  positions: Record<BallId, { x: number; y: number }>;
}

/**
 * A shared-music control event (synchronized YouTube playlist). Relayed to every
 * other client in the room so everyone watches/listens to the same queue in step.
 * - 'playlist' carries the whole queue (add / delete / reorder).
 * - 'play' selects/plays an item from a position; 'pause'/'seek'/'volume' adjust.
 * - 'sync' is a full snapshot for a (re)joining player.
 */
export interface MusicAction {
  action: 'playlist' | 'play' | 'pause' | 'seek' | 'volume' | 'sync';
  /** YouTube video IDs, in queue order (for 'playlist' / 'sync'). */
  videoIds?: string[];
  /** Display titles parallel to `videoIds`. */
  titles?: string[];
  /** Current queue index (for 'playlist' / 'play' / 'sync'). */
  index?: number;
  /** Playback position in milliseconds (for 'play' / 'seek' / 'sync'). */
  positionMs?: number;
  /** Volume 0..100 (for 'volume' / 'sync'). */
  volume?: number;
  /** Whether playback is paused (for 'sync'). */
  paused?: boolean;
}

/**
 * One WebRTC signaling beat for peer-to-peer voice chat, relayed verbatim through
 * the room (the server only tags it with the sender's seat). Exactly one field is
 * meaningful per message:
 * - `ready` / `bye`: a player turned their microphone on / off (presence handshake).
 * - `description`: an SDP offer or answer.
 * - `candidate`: an ICE candidate (`null` signals end-of-candidates).
 * Voice audio itself never touches the server — it flows directly between peers.
 */
export interface VoiceSignal {
  ready?: boolean;
  bye?: boolean;
  description?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit | null;
}

/**
 * Shared room settings the host pushes so both players see the same table and
 * play under the same physics. Relayed verbatim to the other client(s); applied
 * on receipt. (Shots carry their own result, so physics need not match for
 * correctness — this keeps the *feel* identical for fairness.)
 */
export interface RoomConfig {
  /** Table colour / scenery theme. */
  theme: ThemeId;
  /** Room background scene (admin-set, synced to everyone in the room). */
  env: EnvId;
  /** Mutable ball-physics parameters (mirror of the live PHYSICS object). */
  physics: {
    slidingFriction: number;
    rollingFriction: number;
    spinningFriction: number;
    ballBallRestitution: number;
    cushionRestitution: number;
    cushionRestitutionSpeedFalloff: number;
    cushionRestitutionMin: number;
    cushionFriction: number;
    cushionRollGrip?: number;
    followDrawScale: number;
    masseScale: number;
    jumpFactor: number;
    gravity: number;
  };
  /** Gameplay tuning that affects shots (max cue speed + playback speed). */
  maxShotSpeed: number;
  playbackSpeed: number;
  /** House finish rule (4-ball 마지막 3쿠션). Older hosts omit it → default applies. */
  finish?: FinishRule;
}

/** Live aim telegraphy: the shooter's cue pose, streamed so others see it move. */
export interface AimState {
  azimuth: number;
  elevation: number;
  /** Tip contact (side / vertical English). */
  a: number;
  b: number;
  /** Cue pull-back 0..1 (telegraphs the loading stroke). */
  pullback: number;
}

export type ClientMessage =
  // --- accounts / session ---
  | { type: 'guest'; name?: string } // first visit: mint an anonymous account + token
  | { type: 'signup'; name: string; password: string; email: string; token?: string } // claim current guest, or new account (email = unique identity)
  | { type: 'login'; name: string; password: string } // nickname (or email) + password
  | { type: 'auth'; token: string } // resume a saved session (guest or registered)
  | { type: 'logout'; token?: string }
  | { type: 'unlinkIdentity'; provider: string } // detach a social provider (never the last credential)
  // --- presence (dedicated always-on socket; independent of any room) ---
  | { type: 'presence'; name: string; status: PresenceStatus; room?: string } // register / update my status (room = watchable match)
  // --- ranking ---
  | { type: 'leaderboard' } // request the current top-players board
  | { type: 'myProfile' } // request my own rank/level/elo/record card
  // report a finished match; perf = each side's absolute stats (caroms scored
  // toward the handicap + innings taken) for the 애버리지-based 점 tier engine
  | {
      type: 'reportResult';
      winnerId: string;
      loserId: string;
      winner: string;
      loser: string;
      perf?: {
        winner: { caroms: number; innings: number };
        loser: { caroms: number; innings: number };
      };
    }
  // --- client render telemetry (see ClientPerfSample) ---
  | { type: 'clientPerf'; sample: ClientPerfSample }
  // --- admin oversight (ignored unless the connection is an admin account) ---
  | { type: 'adminSubscribe' }
  | { type: 'adminUnsubscribe' }
  | { type: 'playerHistory'; id: string } // admin: request one player's full profile by id
  // --- admin console (/admin): reads and mutations, split so mutations are audited ---
  | { type: 'adminQuery'; rid: number; q: AdminQueryName; params?: Record<string, unknown> }
  | { type: 'adminAction'; rid: number; action: AdminActionName; params?: Record<string, unknown> }
  // --- shot recorder / personal game history ---
  | { type: 'saveGame'; game: GameRecord } // persist a finished game under my account
  | { type: 'gameHistory' } // request my recent games (summaries)
  | { type: 'gameDetail'; id: string } // request one recorded game's full shot log
  // --- item store (gated behind the 'itemStore' feature gate) ---
  | { type: 'store' } // request my coin balance + owned item ids
  | { type: 'purchase'; itemId: string } // spend coins to buy a store item
  | { type: 'equip'; itemId: string | null } // equip an owned cue-ball colour (null = reset to default)
  | { type: 'rename'; name: string } // pay the flat nickname-change price and rename my account
  // --- 오늘의 배치 (daily challenge) ---
  | { type: 'daily' } // request today's layout, my runs, and the board
  | { type: 'dailySubmit'; strokes: number; caroms: number; banks?: number; elapsedMs?: number } // report one finished attempt
  | { type: 'tutorialContent' } // request admin-authored 당구 교실 content
  // --- 건의하기 (one-way bug report / feedback to admins) ---
  | { type: 'feedbackCreate'; text: string }
  // --- head-to-head record vs specific opponents (by account id) ---
  | { type: 'headToHead'; ids: string[] }
  // --- 재대결: invite a player back for another game (opens a room if needed) ---
  | { type: 'rematch'; to: string; variant?: VariantId; identity?: Identity }
  | { type: 'reviewShot'; mode: 'replay' | 'practice'; positions: Record<BallId, { x: number; y: number }>; cue?: CueParams } // end-of-game synced replay / try-this-layout
  // --- lobby / room browser ---
  | { type: 'list' } // request the open-room list
  | { type: 'createRoom'; name: string; format: GameFormat; variant: VariantId; identity: Identity }
  | { type: 'joinRoom'; code: string; identity: Identity }
  | { type: 'quickRoom'; identity: Identity } // auto-match into an open 1v1 room or open one
  | { type: 'leaveRoom' }
  | { type: 'setReady'; ready: boolean }
  // (no 'setLevel': online you always play at your ladder 다마 — the server sets
  // the seat's level from your tier and ignores anything the client claims)
  | { type: 'kick'; targetId: string } // host only, waiting room: remove a member
  | { type: 'setTier'; targetId: string; tier: number } // admin only: set a player's ladder tier
  | { type: 'playersStats'; ids: string[] } // request public stats for the players in a game
  | { type: 'startGame' } // host only: begin once everyone is ready
  | { type: 'setFormat'; format: GameFormat; variant: VariantId } // host only, in waiting room
  | { type: 'setBreak'; style: BreakStyle } // host only: opening style
  | { type: 'setStake'; amount: number } // host only: 코인 bet the loser pays the winner (0 = off)
  | { type: 'sync'; state: SerializedGame } // broadcast a fresh layout (e.g. after a swing break)
  | { type: 'breakstream'; balls: Record<BallId, { x: number; y: number; z: number }>; lever: number } // live swing
  // a chat message, optionally carrying a shot for the room to replay. Either
  // part may stand alone (a shared shot needs no caption).
  | { type: 'chat'; text: string; shot?: SharedShot }
  // private message to one player by nickname; sent on the presence socket so it
  // works outside a room too (see DmLine)
  | { type: 'whisper'; to: string; text: string }
  | { type: 'emote'; emote: string } // fire an emote (sound/emoji) to the room
  // --- in-game (relayed) ---
  | { type: 'rejoin'; code: string; seat: number }
  // a gap in the server's per-room seq counter was noticed on an incoming
  // shot/practice message — ask a peer (via the server) for a fresh full snapshot
  | { type: 'resync' }
  // the active player's own shot clock ran out — self-reported the same way a
  // real shot is, and applied identically on both ends (see applyTimeout)
  | { type: 'timeout' }
  | ({ type: 'shot' } & ShotPayload)
  | { type: 'practice'; cue: CueParams; finalState: Record<BallId, BallSnapshot> } // synced wait-room practice
  | ({ type: 'aim' } & AimState)
  | { type: 'chalk' } // the shooter is chalking their tip (telegraphed to others)
  | { type: 'pointer'; on: boolean; x: number; y: number } // point at the table (on=false clears); server tags the seat
  | { type: 'newgame'; variant: VariantId }
  | ({ type: 'music' } & MusicAction)
  | ({ type: 'voice' } & VoiceSignal)
  | { type: 'config'; config: RoomConfig }
  | { type: 'state'; state: SerializedGame };

export type ServerMessage =
  // --- accounts / session ---
  // name is the nickname; id is the stable account id; guest flags an anonymous
  // session. Sent for guest/auth/signup/login.
  // coins = current 코인 balance; features = feature-gate ids currently enabled
  // server-side (drives which not-yet-stable UI, like the item store, is shown).
  | {
      type: 'authed';
      token: string;
      id: string;
      guest: boolean;
      name: string;
      admin?: boolean;
      coins: number;
      features: string[];
      /** Social providers this server can actually complete a login with. */
      providers?: AuthProvider[];
      /** Providers already linked to this account. */
      identities?: string[];
      /** Equipped cue-ball colour (hex string, no '#'), or null for the default. */
      cueColor?: string | null;
    }
  | { type: 'identities'; providers: string[]; error?: string }
  | { type: 'authError'; message: string }
  | { type: 'kicked' } // the host removed you from the room → back to the browser
  // --- admin oversight ---
  | { type: 'admin'; snapshot: AdminSnapshot }
  | { type: 'playerHistory'; profile: PlayerProfile | null }
  // --- admin console (/admin) ---
  | { type: 'adminResult'; rid: number; q: AdminQueryName; data: unknown }
  | { type: 'adminActionResult'; rid: number; action: AdminActionName; result: { ok?: boolean; error?: string } }
  // --- shot recorder / personal game history ---
  | { type: 'gameHistory'; games: GameSummary[] }
  | { type: 'gameDetail'; game: GameRecord | null }
  // --- item store ---
  | { type: 'store'; coins: number; owned: string[] } // my balance + owned item ids
  | { type: 'purchase'; ok: boolean; coins?: number; owned?: string[]; error?: string } // result of a buy attempt
  | { type: 'equip'; ok: boolean; cueColor?: string | null; error?: string } // result of an equip attempt
  | { type: 'rename'; ok: boolean; name?: string; coins?: number; error?: string } // result of a paid nickname change
  // --- 코인 faucet: what a finished game just paid out (drives the reward toast) ---
  | { type: 'earned'; coins: number; events: CoinEvent[] }
  // --- stake settlement: this seat's side of a finished staked game ---
  | { type: 'stakeResult'; amount: number; won: boolean }
  // --- 오늘의 배치 ---
  | {
      type: 'daily';
      /** Round key (`YYYY-MM-DD#i`) — the layout rerolls periodically. */
      day: string;
      /** Epoch-ms when this layout is replaced by a fresh one. */
      endsAt: number;
      variant: VariantId;
      target: number; // caroms that count as clearing the layout
      maxAttempts: number;
      maxStrokes: number; // an attempt ends here even if the run isn't done
      cueBall: BallId;
      positions: Record<string, { x: number; y: number }>;
      /** 'banks' demands at least `minBanks` of the target caroms be bank shots.
       *  'timedScore'/'timedSpeed' are the two Time Attack rules — see WeeklyMissionWire. */
      ruleType: 'caroms' | 'banks' | 'timedScore' | 'timedSpeed';
      minBanks: number;
      /** Starting cue-ball aim angle override, radians. Null = auto-face the nearest object ball. */
      cueAngle: number | null;
      /** Optional admin-set display name for this week's mission. */
      title: string | null;
      /** 'timedScore' only: the countdown length, seconds. Null for every other rule. */
      timeLimitSec: number | null;
      mine: DailyRun | null;
      entries: DailyEntry[];
    }
  // the outcome of a submitted attempt, carrying a fresh 'daily' snapshot inline
  | {
      type: 'dailyResult';
      ok: boolean;
      error?: string;
      cleared?: boolean;
      improved?: boolean;
      coins?: number;
      events?: CoinEvent[];
      /** Round key (`YYYY-MM-DD#i`) — the layout rerolls periodically. */
      day: string;
      /** Epoch-ms when this layout is replaced by a fresh one. */
      endsAt: number;
      variant: VariantId;
      target: number;
      maxAttempts: number;
      maxStrokes: number;
      cueBall: BallId;
      positions: Record<string, { x: number; y: number }>;
      /** 'banks' demands at least `minBanks` of the target caroms be bank shots.
       *  'timedScore'/'timedSpeed' are the two Time Attack rules — see WeeklyMissionWire. */
      ruleType: 'caroms' | 'banks' | 'timedScore' | 'timedSpeed';
      minBanks: number;
      /** Starting cue-ball aim angle override, radians. Null = auto-face the nearest object ball. */
      cueAngle: number | null;
      /** Optional admin-set display name for this week's mission. */
      title: string | null;
      /** 'timedScore' only: the countdown length, seconds. Null for every other rule. */
      timeLimitSec: number | null;
      mine: DailyRun | null;
      entries: DailyEntry[];
    }
  // Admin-authored 당구 교실 content — public, sent on request (see 'tutorialContent' client msg)
  | { type: 'tutorialContent'; lessons: TutorialLessonWire[]; overrides: TutorialOverrideWire[] }
  // --- 건의하기 ---
  | { type: 'feedbackCreated' } // my report was submitted
  | { type: 'feedbackError'; error: string }
  // --- head-to-head (parallel to the requested ids) ---
  | { type: 'headToHead'; ids: string[]; records: (HeadToHead | null)[] }
  // --- 재대결: someone wants a rematch; `code` is the room they're waiting in ---
  | { type: 'rematchInvite'; from: string; code: string }
  | { type: 'reviewShot'; from: number; mode: 'replay' | 'practice'; positions: Record<BallId, { x: number; y: number }>; cue?: CueParams }
  // --- presence ---
  | { type: 'presence'; users: PresenceUser[] } // the full online-players list
  // --- ranking ---
  | { type: 'leaderboard'; entries: LeaderboardEntry[] } // top players (rating-sorted)
  | { type: 'myProfile'; profile: MyProfile | null } // the requesting player's own card
  | { type: 'playersStats'; stats: (PlayerStats | null)[] } // all-time stats per game player (id order)
  // --- lobby / room browser ---
  | { type: 'rooms'; rooms: RoomInfo[] }
  | { type: 'lobby'; state: LobbyState } // waiting-room snapshot (presence, ready, config)
  | { type: 'chat'; line: ChatLine }
  | ({ type: 'dm' } & DmLine) // a private message (also echoed back to its sender)
  | { type: 'dmError'; to: string; reason: 'offline' | 'flood' } // the DM went nowhere
  | { type: 'emote'; emote: string; seat: number; name: string } // someone fired an emote
  // begins the actual game; carries each member's seat/team so all clients agree
  // (seat -1 = spectator). seat is the room seat (0..players-1).
  | { type: 'gameStart'; variant: VariantId; format: GameFormat; seat: number; names: string[]; levels: number[]; ids?: string[]; cueColors?: string[]; breakStyle: BreakStyle; ranked?: boolean }
  | { type: 'sync'; state: SerializedGame; seq?: number }
  | { type: 'breakstream'; balls: Record<BallId, { x: number; y: number; z: number }>; lever: number }
  // --- in-game (relayed) ---
  | { type: 'joined'; code: string; seat: number; variant: VariantId }
  | ({ type: 'shot' } & ShotPayload)
  | { type: 'practice'; cue: CueParams; finalState: Record<BallId, BallSnapshot>; seq?: number }
  | ({ type: 'aim' } & AimState)
  | { type: 'chalk' }
  | { type: 'pointer'; from: number; on: boolean; x: number; y: number } // a player's table ping (from = seat)
  | { type: 'newgame'; variant: VariantId; seq?: number }
  | { type: 'timeout'; from?: number; seq?: number }
  | ({ type: 'music' } & MusicAction)
  | ({ type: 'voice'; from: Seat } & VoiceSignal)
  | { type: 'config'; config: RoomConfig }
  | { type: 'request-state' }
  | { type: 'state'; state: SerializedGame }
  | { type: 'peer-left'; seat: number }
  | { type: 'peer-rejoined'; seat: number }
  | { type: 'opponent-left' }
  | { type: 'room-closed' }
  // `code` lets the client localize a known failure; `message` is the fallback.
  | { type: 'error'; message: string; code?: 'stakeShort' | 'rankGap'; stake?: number; hostTier?: number; maxGap?: number };
